import BigNumber from 'bignumber.js';
import DOMPurify from 'dompurify';
import { validate as isUuid, version as uuidVersion } from 'uuid';
import { PaymentMethod, PaymentMethodType } from 'store/pay/types';

export const DEFAULT_CLIENT_ID = 100;

export const resolveClientId = (clientIdParam?: number, rawClientId?: string): number => {
  const parsed = rawClientId ? Number(rawClientId) : undefined;
  const fromUrl = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined;
  return clientIdParam ?? fromUrl ?? DEFAULT_CLIENT_ID;
};

export const coerceClientId = (
  clientId: string | number | null | undefined
): number | undefined => {
  if (clientId == null) return undefined;
  if (typeof clientId === 'number') {
    return Number.isSafeInteger(clientId) ? clientId : undefined;
  }
  const trimmed = clientId.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

export const withCoercedClientId = <T extends { client_id?: number | null }>(page: T): T =>
  page.client_id == null
    ? page
    : { ...page, client_id: coerceClientId(page.client_id) as T['client_id'] };

export const withCoercedPages = <T extends { pages?: Array<{ client_id?: number | null }> }>(
  data: T
): T =>
  Array.isArray(data.pages)
    ? { ...data, pages: data.pages.map(withCoercedClientId) as T['pages'] }
    : data;

export const RETIRED_OR_UNEMPLOYED = 'No employer';

export const EMPLOYMENT_FIELD_NAMES = [
  'employer_address1',
  'employer_address2',
  'employer_city',
  'employer_state',
  'employer_zipcode',
  'employer_country',
];

export const noop = () => {
  /* do nothing */
};

export const splitAmountRounded = (totalAmount, numberOfPeople) => {
  /* function to split totalAmount between numberOfPeople so that each person gets an amount rounded 2 decimals */
  const amountPerPerson = new BigNumber(totalAmount)
    .times(100)
    .dividedBy(numberOfPeople)
    .floor()
    .dividedBy(100);
  const remainingAmount = new BigNumber(totalAmount).minus(amountPerPerson.times(numberOfPeople));

  return Array.from({ length: numberOfPeople }, (_, index) => {
    if (index < remainingAmount.times(100).toNumber()) {
      return amountPerPerson.plus(0.01).toNumber();
    }
    return amountPerPerson.toNumber();
  });
};

export const createSessionID = (length: number) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }

  return result;
};

export const getUrlParams = () => {
  const searchParams = getUrlSearchParamsObj();
  const shoppingCartParam = searchParams?.get('shopping-cart');
  const urlShoppingCart = shoppingCartParam
    ? shoppingCartParam.split(',').filter(id => id.trim().length > 0)
    : undefined;
  // Links can be hand-edited or truncated - anything but the server's v4 id means no cart.
  const donorCartParam = searchParams?.get('donor_cart');
  const urlDonorCart =
    donorCartParam && isUuid(donorCartParam) && uuidVersion(donorCartParam) === 4
      ? donorCartParam
      : undefined;

  return {
    urlRef: searchParams?.get('ref')?.replace('/', ''),
    urlPartner: searchParams?.get('p')?.replace('/', ''),
    urlIssue: searchParams?.get('issue')?.replace('/', ''),
    urlAmount: searchParams?.get('amount')?.replace('/', ''),
    urlTags: searchParams.getAll('tags'),
    urlDonateAgain: searchParams?.get('donate_again')?.replace('/', ''),
    urlEvent: searchParams?.has('event'),
    urlRecipients: searchParams?.get('recipients')?.replace('/', ''),
    urlDonateMethod: searchParams?.get('method')?.replace('/', ''),
    urlDonorSelectedMethod: searchParams?.get('m')?.replace('/', '') as PaymentMethod,
    urlDonorSelectedMethodType: searchParams?.get('mt')?.replace('/', '') as PaymentMethodType,
    urlRecurringInterval: searchParams?.get('interval')?.replace('/', ''),
    urlClientId: searchParams?.get('client_id')?.replace('/', ''),
    urlPrimaryColor: searchParams?.get('primary_color')?.replace('/', ''),
    urlSecondaryColor: searchParams?.get('secondary_color')?.replace('/', ''),
    urlExpressCheckout: searchParams?.get('express_checkout')?.replace('/', ''),
    urlShoppingCart,
    urlDonorCart,
    urlQuery: searchParams?.get('q')?.replace('/', ''),
    urlScroll: searchParams?.get('scroll')?.replace('/', ''),
  };
};

export const getShowAllDonateMethods = (donateMethod?: string) => {
  const validDonateMethods = ['card', 'ach', 'daf', 'pledge'];
  return !donateMethod || !validDonateMethods.includes(donateMethod);
};

export const numberWithDot = (x: number) => {
  const withComma = x?.toString().replace(',', '.');

  return withComma?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || ' ...';
};

export function wait(timeout: number) {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
}

export const formatNumberAsCurrency = (num: number, options: object = {}) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    //@ts-ignore
    trailingZeroDisplay: 'stripIfInteger',
    ...options,
  }).format(num);
};

export const formatNumber = (num: number, options: object = {}) => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    //@ts-ignore
    trailingZeroDisplay: 'stripIfInteger',
    ...options,
  }).format(num);
};

export const getPathWithRef = (path: string) => {
  const { urlRef } = getUrlParams();
  const char = path.includes('?') ? '&' : '?';
  const ref = urlRef ? `${char}ref=${urlRef}` : '';
  return `${path}${ref}`;
};

export const abbreviateNum = (num: number) => {
  const value = Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);

  return isNaN(parseFloat(value)) ? '0' : value;
};

export const roundNumberToHundredths = (num: number | string) => {
  const numFloat = typeof num === 'string' ? Number.parseFloat(num) : num;
  return Math.round(numFloat * 100) / 100;
};

// Compact currency for space-constrained displays: whole dollars (no cents)
// below $10,000 (e.g. $3,022), abbreviated to one-decimal compact form at or
// above (e.g. $15.3K, $1.5M).
export const formatCurrencyCompact = (num: number): string => {
  const rounded = Math.round(num);
  return Math.abs(rounded) >= 10000
    ? `$${abbreviateNum(rounded)}`
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(rounded);
};

export const getUrlSearchParamsObj = () => new URL(window.location.href).searchParams;

export const getUrlSearchParamsStr = () => getUrlSearchParamsObj().toString();

export const getUrlSearchParamsObjNoMethod = () => {
  const searchParams = new URL(window.location.href).searchParams;
  searchParams.delete('m');
  searchParams.delete('mt');
  return searchParams;
};

export const getUrlSearchParamsStrNoMethod = () => getUrlSearchParamsObjNoMethod().toString();

export const limitChars = (value: string, limit: number) => {
  if (value?.length > limit) {
    return `${value.substring(0, limit)}...`;
  } else {
    return value;
  }
};

export function getObjectDisplayName<T extends object>(key: string, obj: T) {
  if (Object.keys(obj).includes(key)) {
    return obj[key];
  }
  return '-';
}

export const isKeyInObject = (key: string, obj: object) => Object.keys(obj).includes(key);

export const isObjectEmpty = (obj: object) => Object.keys(obj).length === 0;

export const required = (value: any) => (value ? undefined : 'Required');

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });
};

//Dashboard returns different symbol when add code to rich text
export const codeFormatter = (text: string) => {
  return text?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>');
};

// Allow-list for sanitizeHtml derived from enabled formats in admin's Quill
// editor (admin/src/components/QuillInput), plus tags used by legacy pages.
// Kept in sync with the twin in admin/src/helpers/utils.ts.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'center',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'u',
    'ul',
  ],
  // `class` carries every Quill format that isn't a tag of its own (e.g. ql-align-*)
  // Quill sets target="_blank" and rel="noopener noreferrer" on every link
  // it creates, so dropping those two would change existing link behavior.
  // Deliberately absent: `id` (DOM clobbering).
  ALLOWED_ATTR: ['class', 'href', 'rel', 'target', 'title', 'style'],
};

// `style` is allowed only to carry text-align, which legacy pages rely on for layout.
// Every other declaration is stripped.
const ALLOWED_STYLE_DECLARATION = /^text-align:\s*(left|right|center|justify)$/i;

DOMPurify.addHook('afterSanitizeAttributes', node => {
  const style = node.getAttribute?.('style');
  if (!style) return;

  const kept = style
    .split(';')
    .map(declaration => declaration.trim())
    .filter(declaration => ALLOWED_STYLE_DECLARATION.test(declaration));

  if (kept.length) {
    node.setAttribute('style', `${kept.join('; ')};`);
  } else {
    node.removeAttribute('style');
  }
});

export const sanitizeHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '', SANITIZE_CONFIG);

export function getRowLength<T extends object>(size: number, recipients: T[]) {
  const newArr = Array<T[]>();
  // TIP: For mobile and tablet make different lines of "length" items each
  for (let i = 0; i < Math.ceil(recipients?.length / size); i++) {
    newArr.push(recipients?.slice(i * size, i * size + size));
  }
  return newArr;
}

export const getIsRetiredOrUnemployed = (employer: string, occupation: string) => {
  return employer === RETIRED_OR_UNEMPLOYED && occupation === RETIRED_OR_UNEMPLOYED;
};

export const getFormattedDate = (date?: string | number | null) => {
  if (date && date !== 'NA') {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } else {
    return date;
  }
};

export const buildQueryParams = (params: Record<string, any>): string => {
  const paramStrings: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Handle array values (for multi-value params like recipient_ids)
      if (Array.isArray(value)) {
        value.forEach(item => {
          paramStrings.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        });
      } else {
        paramStrings.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
  });

  return paramStrings.length > 0 ? `?${paramStrings.join('&')}` : '';
};

export const capitalizeString = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
